import AsyncStorage from '@react-native-async-storage/async-storage';
import {Platform} from 'react-native';
import {getBigStatisticsData} from './printerLibrary/oldPrinter';
import {sendPrinterAndroid} from './printerLibrary/printer';
import {getUserInfo} from './storage';
import safeJsonParse from './safeJsonParse';

const DEFAULT_PRINTER_CONFIG = {
    timeout: 400,
    autoCut: true,
    mmFeedPaper: 60,
    printerDpi: 203,
    // Keep statistics a bit narrower so labels and totals stay on one line on 80mm printers.
    printerWidthMM: 72,
    printerNbrCharactersPerLine: 44,
};

const normalizePrinter = (printer = {}) => {
    if (printer?.ip) {
        return {
            key: `tcp:${printer.ip}:${printer.port || 9100}`,
            config: {
                ...DEFAULT_PRINTER_CONFIG,
                ip: printer.ip,
                port: Number(printer.port) || 9100,
            },
        };
    }

    const macAddress = printer?.address || printer?.macAddress || printer?._nativeDevice?.address;
    if (macAddress) {
        return {
            key: `bt:${macAddress}`,
            config: {
                macAddress,
            },
        };
    }

    return null;
};

export const loadStatisticsPrinters = async () => {
    const uniquePrinters = new Map();

    const pushPrinter = (printer) => {
        const normalized = normalizePrinter(printer);
        if (normalized) {
            uniquePrinters.set(normalized.key, normalized.config);
        }
    };

    const printerDeviceRaw = await AsyncStorage.getItem('printerDevice');
    const printerDevice = safeJsonParse(printerDeviceRaw);
    if (printerDevice) {
        pushPrinter(printerDevice);
    }

    const printerEventsRaw = await AsyncStorage.getItem('printerEvents');
    const printerEvents = safeJsonParse(printerEventsRaw, []);

    // Only use receipt and cashRegister printers – never terminal (kitchen) printers.
    const preferredEvents = ['receipt', 'cashRegister'];
    preferredEvents.forEach((eventType) => {
        const event = printerEvents.find((printerEvent) => printerEvent?.type === eventType);
        if (Array.isArray(event?.printers)) {
            event.printers.forEach(pushPrinter);
        }
    });

    return Array.from(uniquePrinters.values());
};

export const printStatisticsReport = async ({ orders = [], statistics = {} }) => {
    if (Platform.OS !== 'android') {
        throw new Error('printing_unavailable_platform');
    }

    const printers = await loadStatisticsPrinters();
    if (!printers.length) {
        throw new Error('no_printer');
    }

    const restaurantData = await AsyncStorage.getItem('restaurant');
    const restaurant = safeJsonParse(restaurantData, {});
    const user = (await getUserInfo()) || {};

    const payload = getBigStatisticsData(
        {
            orders,
            priceSummary: {
                productsSum: Number(statistics?.productsSum) || 0,
                discountsSum: Number(statistics?.discountsSum) || 0,
                serviceFeeSum: Number(statistics?.serviceFeePrice) || 0,
                deliveryFeeSum: orders.reduce((total, order) => total + Number(order?.deliveryFee || 0), 0),
                ordersSum: Number(statistics?.finalSum) || 0,
            },
            vatSummary: {
                vat: statistics?.summary?.vat || {},
                net: statistics?.summary?.net || {},
                gross: statistics?.summary?.gross || {},
            },
        },
        restaurant,
        user
    );

    let printedCount = 0;
    for (const printer of printers) {
        const result = await sendPrinterAndroid(payload, printer, true, false);
        if (result === true) {
            printedCount += 1;
        }
    }

    if (!printedCount) {
        throw new Error('printing_failed');
    }

    return printedCount;
};

